// API base: dev → Vite proxies /api to the Worker (:8787); prod → VITE_API_BASE_URL.
const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const TOKEN_KEY = "tshirtbar_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function api<T = any>(
  path: string,
  opts: { method?: string; body?: unknown; auth?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  const token = getToken();
  if (opts.auth !== false && token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}/api/v1${path}`, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(res.status, data?.error || `Request failed (${res.status})`);
  return data as T;
}

// ── Types ───────────────────────────────────────────────────
export interface User {
  id: string;
  email: string;
  name: string | null;
  role: "user" | "superadmin";
}
export interface Impersonating {
  by: string;
  email: string;
}

// ── Auth ────────────────────────────────────────────────────
export const authApi = {
  register: (email: string, password: string, name: string) =>
    api<{ token: string; user: User }>("/auth/register", { body: { email, password, name } }),
  login: (email: string, password: string) =>
    api<{ token: string; user: User }>("/auth/login", { body: { email, password } }),
  me: () => api<{ user: User; impersonating: Impersonating | null }>("/auth/me"),
};

// ── Current user's data ─────────────────────────────────────
export interface OrderItemInput {
  design_id?: string;
  kind?: "tshirt" | "gangsheet";
  description: string;
  qty?: number;
  unit_price?: number;
  print_mode?: "print_on_garment" | "print_only";
  size_label?: string;
  garment_color?: string;
  thumbnail?: string;
}
export interface DeliveryInput {
  method?: "courier" | "pudo";
  phone?: string;
  address?: string;
  locker_code?: string;
  locker_name?: string;
}
export interface DesignInput {
  name: string;
  product?: string;
  kind?: "tshirt" | "gangsheet";
  color?: string;
  width_mm?: number;
  height_mm?: number;
  data: string;
  thumbnail?: string;
}

export interface Profile {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  town?: string;
  locker_code?: string;
  locker_name?: string;
  delivery_method?: "courier" | "pudo";
}

export const meApi = {
  profile: () => api<{ profile: Profile }>("/me/profile"),
  updateProfile: (p: Partial<Profile>) => api("/me/profile", { method: "PUT", body: p }),
  quotes: () => api<{ quotes: any[] }>("/me/quotes"),
  designs: () => api<{ designs: any[] }>("/me/designs"),
  design: (id: string) => api<{ design: any }>(`/me/designs/${id}`),
  createDesign: (d: DesignInput) => api<{ id: string }>("/me/designs", { body: d }),
  updateDesign: (id: string, d: Partial<DesignInput>) => api(`/me/designs/${id}`, { method: "PUT", body: d }),
  deleteDesign: (id: string) => api(`/me/designs/${id}`, { method: "DELETE" }),
  artwork: () => api<{ artwork: any[] }>("/me/artwork"),
  uploadArtwork: (a: { filename: string; content_type: string; data: string }) =>
    api<{ id: string }>("/me/artwork", { body: a }),
  deleteArtwork: (id: string) => api(`/me/artwork/${id}`, { method: "DELETE" }),
  orders: () => api<{ orders: any[] }>("/me/orders"),
  order: (id: string) => api<{ order: any; items: any[]; payments: any[] }>(`/me/orders/${id}`),
  createOrder: (o: { items: OrderItemInput[]; customer_note?: string; delivery?: DeliveryInput; delivery_fee?: number }) =>
    api<{ id: string; order_number: string }>("/me/orders", { body: o }),
  updateOrder: (id: string, o: { items?: OrderItemInput[]; customer_note?: string; delivery?: DeliveryInput }) =>
    api(`/me/orders/${id}`, { method: "PUT", body: o }),
  acceptOrder: (id: string) => api(`/me/orders/${id}/accept`, { body: {} }),
};

// ── Pudo lockers (public read) ──────────────────────────────
export const lockersApi = {
  list: (q?: string) => api<{ lockers: any[]; source: string }>(`/lockers${q ? `?q=${encodeURIComponent(q)}` : ""}`, { auth: false }),
};

// ── Pricing (public read) ───────────────────────────────────
export const pricingApi = {
  get: () => api<{ pricing: any }>("/pricing", { auth: false }),
};

// ── Garment colours (public read) ──────────────────────────
export interface GarmentColor {
  name: string;
  hex: string;
}
export const colorsApi = {
  get: () => api<{ colors: GarmentColor[] }>("/colors", { auth: false }),
};

// ── Admin (superadmin only) ─────────────────────────────────
export const adminApi = {
  stats: () => api<{ stats: any }>("/admin/stats"),
  users: () => api<{ users: any[] }>("/admin/users"),
  user: (id: string) => api<{ user: any; designs: any[]; artwork: any[]; orders: any[]; quotes: any[] }>(`/admin/users/${id}`),
  setRole: (id: string, role: "user" | "superadmin") =>
    api(`/admin/users/${id}/role`, { body: { role } }),
  deleteUser: (id: string) => api(`/admin/users/${id}`, { method: "DELETE" }),
  impersonate: (id: string) => api<{ token: string; user: User }>(`/admin/impersonate/${id}`, { body: {} }),
  quotes: () => api<{ quotes: any[] }>("/admin/quotes"),
  orders: () => api<{ orders: any[] }>("/admin/orders"),
  order: (id: string) => api<{ order: any; items: any[]; files: any[]; payments: any[] }>(`/admin/orders/${id}`),
  quoteOrder: (id: string, quote_amount: number) => api(`/admin/orders/${id}/quote`, { body: { quote_amount } }),
  payOrder: (id: string, body: { amount?: number; method?: string; note?: string } = {}) =>
    api(`/admin/orders/${id}/pay`, { body }),
  unpayOrder: (id: string) => api(`/admin/orders/${id}/unpay`, { body: {} }),
  setOrderStatus: (id: string, status: string) =>
    api(`/admin/orders/${id}/status`, { body: { status } }),
  updateOrder: (id: string, body: Record<string, unknown>) => api(`/admin/orders/${id}`, { method: "PUT", body }),
  uploadOrderFile: (id: string, f: { order_item_id?: string; filename: string; content_type?: string; data: string }) =>
    api<{ id: string }>(`/admin/orders/${id}/files`, { body: f }),
  orderFile: (id: string, fid: string) => api<{ file: any }>(`/admin/orders/${id}/files/${fid}`),
  deleteOrderFile: (id: string, fid: string) => api(`/admin/orders/${id}/files/${fid}`, { method: "DELETE" }),
  payments: () => api<{ payments: any[] }>("/admin/payments"),
  syncLockers: () => api<{ count: number }>("/admin/lockers/sync", { body: {} }),
  getPudoToken: () =>
    api<{ configured: boolean; source: string; value: string | null; masked: string | null }>(
      "/admin/pudo-token"
    ),
  savePudoToken: (token: string) => api<{ configured: boolean }>("/admin/pudo-token", { method: "PUT", body: { token } }),
  getPricing: () => api<{ pricing: any }>("/admin/pricing"),
  savePricing: (pricing: any) => api<{ pricing: any }>("/admin/pricing", { method: "PUT", body: pricing }),
  getColors: () => api<{ colors: any[] }>("/admin/colors"),
  saveColors: (colors: any[]) => api<{ colors: any[] }>("/admin/colors", { method: "PUT", body: colors }),
  designs: () => api<{ designs: any[] }>("/admin/designs"),
  artwork: () => api<{ artwork: any[] }>("/admin/artwork"),
};

// ── Public quote (landing page) ─────────────────────────────
export interface QuotePayload {
  name: string;
  phone: string;
  email: string;
  message: string;
}
export async function submitQuote(payload: QuotePayload) {
  return api<{ ok: boolean; id?: string }>("/quotes", { body: payload });
}
